import {
  Collapse,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Portal,
} from '@chakra-ui/react';
import { ChevronDownIcon, CloseIcon } from '@sketchnote/icons';
import { priorityOptions } from '@sketchnote/shared-components';
import { GridApi, RowDropZoneParams } from 'ag-grid-community';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import classNames from 'classnames';
import { format } from 'date-fns';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBoardStore } from 'src/features/boards';
import {
  AssigneeCellEditor,
  DateCellEditor,
  PriorityCellEditor,
} from 'src/features/list/CellEditors';
import { OpenTaskColumnButton } from 'src/features/list/ListCellRenders';
import { getFormattedValue } from 'src/utils/getFormattedValue';
import useOnClickOutside from 'use-onclickoutside';

import styles from '../../../list/styles/ListGridView.module.css';
import { addItemToSprint, createBacklogItem, createEpic } from '../../api/useAgileService';
import { Sprint, SprintTask, useAgileStore } from '../../store';
import { EditSprint } from '../sprint/EditSprint';
import { SprintAction } from '../sprint/SprintAction';
import CompleteSprint from '../sprint/SprintComplete';
import StartSprint from '../sprint/SprintStart';
import { EpicCellEditor, StatusCellEditor, TypeCellEditor } from './CellEditors';
import { EpicCellRenderer, StatusRenderer, TypeRenderer } from './CellRenderers';

export interface TableProps {
  sprint: Sprint;
}

export const SprintTable = ({ sprint }: TableProps) => {
  const [collapse, setCollapse] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const {
    deleteSprint,
    onUpdateItemField,
    removeSprintItem,
    addSprintItem,
    updateSprintItem,
    sprints,
    moveSprintItem,
    fileId,
    assignTaskToEpic,
    changeSprintItemStatus,
  } = useAgileStore((s) => ({
    deleteSprint: s.deleteSprint,
    onUpdateItemField: s.onUpdateItemField,
    removeSprintItem: s.removeSprintItem,
    addSprintItem: s.addSprintItem,
    updateSprintItem: s.updateSprintItem,
    sprints: s.sprints,
    moveSprintItem: s.moveSprintItem,
    fileId: s.fileId,
    assignTaskToEpic: s.assignTaskToEpic,
    changeSprintItemStatus: s.changeSprintItemStatus,
  }));

  const search = useBoardStore((s) => s.search);
  // const { filters, labels } = useBoardStore((s) => ({ filters: s.filters, labels: s.labels }));
  const [columnDefs, setColumnDefs] = useState<AgGridReactProps['columnDefs']>([]);
  const [api, setAPI] = useState<GridApi>();
  const [rowData, setRowData] = useState<AgGridReactProps['rowData']>([]);
  const router = useRouter();
  const orgId = router?.query?.orgId;
  const mode = router?.query?.mode;
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const [addNewTaskSaving, setAddNewTaskSaving] = useState(false);
  const [addingNewTask, setAddingNewTask] = useState(false);
  const [newTask, setNewTask] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const isBacklog = sprint._id === 'backlog';
  const isEpics = sprint._id === 'epics';
  const isActualSprint = !isBacklog && !isEpics;

  const handleCloseAddTask = () => {
    setAddingNewTask(false);
    setNewTask('');
  };

  const handleAddTask = async (e) => {
    e.preventDefault();
    if (newTask.trim() === '') return;
    setAddNewTaskSaving(true);
    if (!sprint._id) return;
    if (isBacklog) {
      /** Add backlog task */
      const newTaskRes = await createBacklogItem(newTask, fileId);
      addSprintItem('backlog', newTaskRes);
    } else if (isEpics) {
      /** Add epic */
      const newTaskRes = await createEpic(newTask, '', fileId);
      addSprintItem('epics', newTaskRes);
    } else {
      /** Add sprint task */
      const newTaskRes = await addItemToSprint(sprint._id, newTask, '');
      addSprintItem(sprint._id, newTaskRes);
    }
    // setAddingNewTask(false);
    setAddNewTaskSaving(false);
    setNewTask('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const isGridReady = !!api;

  /** Adding grid dropzones */
  useEffect(() => {
    if (isGridReady) {
      [{ _id: 'backlog', title: 'Backlog' }, ...sprints].forEach((spr) => {
        if (spr._id === sprint._id) return;
        const containerEl = document.getElementById(`grid-container-${spr._id}`);
        if (!containerEl) return;
        const dropZone: RowDropZoneParams = {
          getContainer: () => {
            return containerEl;
          },
          onDragStop: (params) => {
            handleDropIntoSection(
              params.nodes.map((n) => n.data as SprintTask),
              sprint._id,
              spr._id
            );
          },
        };
        api.addRowDropZone(dropZone);
      });
    }
  }, [sprints, isGridReady]);

  useOnClickOutside(gridContainerRef, () => {
    if (api) {
      api.deselectAll();
      api.stopEditing();
    }
  });

  const handleSprintAction = (e, event) => {
    event.stopPropagation();
    if (e.value === 'delete') {
      setIsDeleteModalOpen(true);
    } else {
      setIsEditModalOpen(true);
    }
  };

  const handleClose = () => {
    setIsEditModalOpen(false);
  };

  const handleDeleteSprint = () => {
    deleteSprint(sprint._id);
  };

  const handleEpicChange = useCallback(
    (item, value) => {
      if (item.epicRef === value) return;
      assignTaskToEpic(item._id, sprint._id, value);
    },
    [sprint._id]
  );

  const handleTypeChange = useCallback(
    (item, value) => {
      if (item.type === value) return;
      const newItem = {
        ...item,
        type: value,
        name: [
          {
            type: 'p',
            children: [
              {
                text: item.name,
              },
            ],
          },
        ],
      };
      onUpdateItemField({
        item: newItem,
        keys: ['type'],
      });
      if (item.type !== 'epic') {
        if (value === 'epic') {
          removeSprintItem(sprint._id, item._id);
          addSprintItem('epics', newItem);
        } else {
          updateSprintItem(sprint._id, newItem);
        }
      } else if (item.type === 'epic') {
        if (value !== 'epic') {
          removeSprintItem('epics', item._id);
          addSprintItem('backlog', newItem);
        } else {
          updateSprintItem('epics', newItem);
        }
      }
    },
    [sprint._id]
  );

  // const handleMarkAsComplete = useCallback(
  //   (newItem) => {
  //     onUpdateItemField({
  //       item: newItem,
  //       keys: ['completed'],
  //     });
  //     updateSprintItem(sprint._id, newItem);
  //   },
  //   [sprint._id]
  // );

  const handleTitleChange = useCallback(
    (item) => {
      const newItem = {
        ...item,
        name: [
          {
            type: 'p',
            children: [
              {
                text: item.name,
              },
            ],
          },
        ],
      };
      onUpdateItemField({
        item: newItem,
        keys: ['name'],
      });
      updateSprintItem(sprint._id, newItem);
    },
    [sprint._id]
  );

  const handleStoryPointsChange = useCallback(
    (newItem) => {
      onUpdateItemField({
        item: newItem,
        keys: ['storyPoint'],
      });
      updateSprintItem(sprint._id, newItem);
    },
    [sprint._id]
  );

  const handleAssignedDatesChange = useCallback(
    (item, value) => {
      let startDate;
      let endDate;
      if (value.length === 1) {
        startDate = null;
        endDate = value[0];
      } else if (value.length === 2) {
        startDate = value[0];
        endDate = value[1];
      }

      const newItem = { ...item, start_date: startDate, end_date: endDate, _id: item._id };
      onUpdateItemField({
        item: newItem,
        keys: ['start_date', 'end_date'],
      });
      updateSprintItem(sprint._id, newItem);
    },
    [sprint._id]
  );

  const handlePriorityChange = useCallback(
    (item: SprintTask, value: 'low' | 'medium' | 'high') => {
      const newItem = { ...item, priority: value };
      onUpdateItemField({
        item: newItem,
        keys: ['priority'],
      });
      updateSprintItem(sprint._id, newItem);
    },
    [sprint._id]
  );

  const handleAssigneeChange = useCallback(
    (item: SprintTask, value: any) => {
      const newItem = { ...item, assignee: value };
      onUpdateItemField({
        item: newItem,
        keys: ['assignee'],
      });
      updateSprintItem(sprint._id, newItem);
    },
    [sprint._id]
  );

  const onOpenTask = (taskId) => {
    router.push({
      pathname: `/${mode}/${orgId}/files/${fileId}`,
      query: { taskId, view: router.query?.view, type: 'agile' },
    });
  };

  /** setting column definitions */
  useEffect(() => {
    const derivedColumns: AgGridReactProps['columnDefs'] = [
      // {
      //   field: 'completed',
      //   headerName: 'Completed',
      //   cellRenderer: 'agCheckboxCellRenderer',
      //   width: 45,
      //   maxWidth: 45,
      //   editable: true,
      //   cellDataType: 'boolean',
      //   onCellValueChanged: ({ data }) => {
      //     handleMarkAsComplete(data);
      //   },
      // },
      {
        field: 'name',
        headerName: 'Item name',
        editable: true,
        singleClickEdit: true,
        onCellValueChanged: ({ data }) => {
          handleTitleChange(data);
        },
        flex: 1,
        minWidth: 200,
        rowDrag: true,
        checkboxSelection: true,
      },
      {
        field: 'column',
        headerName: 'Status',
        cellRenderer: StatusRenderer,
        initialWidth: 150,
        maxWidth: 150,
        cellStyle: { padding: 0 },
        cellEditor: (props) => (
          <StatusCellEditor
            {...props}
            onChange={(res) => {
              const task = props.data as SprintTask;
              changeSprintItemStatus(task._id, sprint._id, task.column, res._id, task.board);
            }}
          />
        ),
        editable: true,
        singleClickEdit: true,
        cellEditorPopup: true,
      },
      {
        field: '_id',
        headerName: 'Open Task',
        cellRenderer: OpenTaskColumnButton,
        initialWidth: 45,
        maxWidth: 45,
        onCellClicked: ({ value }) => {
          onOpenTask(value);
        },
      },
      {
        field: 'type',
        headerName: 'Type',
        editable: true,
        singleClickEdit: true,
        initialWidth: 100,
        cellRenderer: TypeRenderer,
        cellEditorPopup: true,
        cellEditorPopupPosition: 'under',
        cellEditor: (props) => (
          <TypeCellEditor
            {...props}
            onChange={(val) => {
              handleTypeChange(props.data, val);
            }}
          />
        ),
      },
      {
        field: 'epicRef',
        headerName: 'Epic',
        editable: true,
        singleClickEdit: true,
        initialWidth: 100,
        cellRenderer: EpicCellRenderer,
        cellEditor: (props) => (
          <EpicCellEditor {...props} onChange={(val) => handleEpicChange(props.data, val)} />
        ),
        cellEditorPopup: true,
        cellEditorPopupPosition: 'under',
        hide: isEpics,
      },
      {
        field: 'storyPoint',
        headerName: 'Story points',
        editable: true,
        singleClickEdit: true,
        initialWidth: 140,
        cellDataType: 'number',
        onCellValueChanged: ({ data }) => {
          handleStoryPointsChange(data);
        },
      },
      {
        field: 'assignee',
        headerName: 'Assigned to',
        valueFormatter: ({ value }) => {
          return value?.map((i) => i?.firstname || '').join(', ') || '';
        },
        editable: true,
        cellEditor: (props) => (
          <AssigneeCellEditor
            {...props}
            onChange={(val) => handleAssigneeChange(props.data, val)}
          />
        ),
        singleClickEdit: true,
        cellEditorPopup: true,
        cellEditorPopupPosition: 'under',
        // valueGetter: ({ data }) => data.assignee.map((i) => i.firstname).join(', ') || '-',
        // cellRenderer: AssigneeCellRenderer,
      },
      {
        field: 'assignDate',
        headerName: 'Assigned dates',
        valueFormatter: ({ value }) =>
          value?.map((i) => format(new Date(i), 'dd/MM/yyyy')).join('-') || '',
        editable: true,
        cellEditor: (props) => <DateCellEditor {...props} onChange={handleAssignedDatesChange} />,
        cellEditorPopup: true,
        cellEditorPopupPosition: 'under',
        singleClickEdit: true,
        initialWidth: 160,
      },
      {
        field: 'priority',
        valueFormatter: ({ value }) =>
          value?.charAt(0).toUpperCase() + value?.slice(1).toLowerCase() || '',
        cellStyle: (params) => {
          if (params?.data?.priority) {
            let backgroundColor = '#FFFFFF';
            let color = '#000000';
            const selectedPriorityOption = priorityOptions.find(
              (p) => p.value === params?.data?.priority
            );
            if (selectedPriorityOption) {
              backgroundColor = selectedPriorityOption.bgColor;
              color = selectedPriorityOption.color;
            }

            return {
              backgroundColor,
              color,
              textAlign: 'center',
            };
          }
        },
        editable: true,
        cellEditor: (props) => <PriorityCellEditor {...props} onChange={handlePriorityChange} />,
        singleClickEdit: true,
        cellEditorPopup: true,
        cellEditorPopupPosition: 'under',
        initialWidth: 120,
      },
    ];
    setColumnDefs(derivedColumns);
  }, []);

  /** Transforming SprintTask to be ag-grid readable */
  const sprintDataRowTransformer = useCallback(
    (sprint: Sprint) => {
      const reducedAndFlattenedColumnItems = sprint.items.map((task, sIndex) => ({
        ...task,
        colName: task.name,
        colId: task._id,
        source: { id: sprint._id, index: sIndex },
        assignDate: [task.start_date, task.end_date].filter(Boolean),
        storyPoint: task.storyPoint ? parseInt(task.storyPoint as any) : undefined,
      }));

      const transformedColumnItems: AgGridReactProps['rowData'] =
        reducedAndFlattenedColumnItems?.map((item) => ({
          ...item,
          name: getFormattedValue(item.name),
        }));

      return transformedColumnItems;
    },
    [sprint, sprint.items]
  );

  const handleDropIntoSection = (rows: SprintTask[], sourceSectionId, destSectionId) => {
    rows.forEach((row) => {
      moveSprintItem(row._id, sourceSectionId, destSectionId);
    });
  };

  const allRowItems = useMemo(() => sprintDataRowTransformer(sprint), [sprint.items]);

  useEffect(() => {
    if (isGridReady) {
      setRowData(
        allRowItems.filter((item) => item.name.toLowerCase().includes(search.toLowerCase()))
      );
    }
  }, [isGridReady, sprint.items, search]);

  const tableContainerHeight = useMemo(() => {
    const headerHeight = 51;
    const rowHeight = 42;
    if (!rowData) return headerHeight;
    return rowData.length * rowHeight + headerHeight;
  }, [rowData?.length]);

  return (
    <div
      className={`w-full no-scrollbar overflow-visible ${
        sprint.isSprintRunning === 2 && 'opacity-50'
      }`}
      id={`grid-container-${sprint._id}`}
    >
      <div className="w-full mt-32">
        <div
          role="button"
          className={classNames(
            'w-full flex items-center justify-between pb-10',
            !collapse && 'border-b'
          )}
          onClick={() => {
            setCollapse(!collapse);
          }}
        >
          <div className="flex">
            <span
              style={{
                transform: collapse ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'all 0.5s',
              }}
              className={'mt-4 block'}
            >
              <ChevronDownIcon />
            </span>
            <span
              style={{ fontWeight: '450' }}
              className="px-8 py-2 rounded-sm font-medium "
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              {sprint.title}
            </span>
            {isActualSprint && (
              <span className="ml-8">
                {sprint.isSprintRunning === 1 ? (
                  <span className="text-white bg-primary-500 py-3 px-5 rounded-sm text-xss">
                    ONGOING
                  </span>
                ) : sprint.isSprintRunning !== 2 ? (
                  <StartSprint sprint={sprint} />
                ) : (
                  <></>
                )}
              </span>
            )}
          </div>
          {isActualSprint && (
            <div className="mr-10 text-gray-500 flex gap-8 items-center">
              {sprint.isSprintRunning === 1 && <CompleteSprint id={sprint._id} />}
              <SprintAction onClick={handleSprintAction} />
            </div>
          )}
        </div>
      </div>
      <div className="w-full overflow-visible no-scrollbar">
        <Collapse in={collapse} className="overflow-visible no-scrollbar">
          <div className="custom-table-scroll">
            <div
              className={`ag-theme-alpine m-auto rounded-md ${styles['custom-ag-theme']}`}
              style={{ height: tableContainerHeight }}
              ref={gridContainerRef}
            >
              <AgGridReact
                domLayout="normal"
                gridId={sprint._id}
                getRowId={(task) => `${sprint._id}-${task.data._id}`}
                rowData={rowData}
                columnDefs={columnDefs}
                onGridReady={(params) => {
                  setAPI(params.api);
                }}
                suppressMoveWhenRowDragging
                suppressRowClickSelection
                rowSelection="multiple"
                rowDragManaged
                rowDragMultiRow
                suppressNoRowsOverlay
                defaultColDef={{
                  resizable: true,
                }}
              ></AgGridReact>
            </div>
            <div className=" flex h-50 items-center">
              {addingNewTask ? (
                <form className="flex" onSubmit={handleAddTask}>
                  <input
                    ref={inputRef}
                    className=" border rounded-sm w-330 mr-12 px-12 py-5 text-sm"
                    type="text"
                    value={newTask}
                    onChange={(e) => {
                      setNewTask(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        handleCloseAddTask();
                      }
                    }}
                    placeholder="Untitled task"
                    disabled={addNewTaskSaving}
                    autoFocus
                  />
                  <button
                    type="submit"
                    className={classNames(
                      'bg-gray-200 px-12 text-xs text-gray-700 hover:bg-gray-300 border border-gray-300 rounded-sm mr-6',
                      {
                        ' animate-pulse': addNewTaskSaving,
                      }
                    )}
                    disabled={addNewTaskSaving}
                  >
                    {addNewTaskSaving ? 'Saving...' : 'Save'}
                  </button>
                  <button onClick={handleCloseAddTask} disabled={addNewTaskSaving}>
                    <CloseIcon className="ml-5 text-gray-500 text-xss hover:text-gray-600" />
                  </button>
                </form>
              ) : (
                <button
                  className="flex bg-gray-200 hover:bg-gray-300 border border-gray-300 items-center text-sm text-gray-700 px-8 py-2 rounded-sm"
                  tabIndex={-1}
                  onClick={() => {
                    setAddingNewTask(true);
                  }}
                >
                  {/* <AddIcon wrapperClass="text-gray-700 text-sm mr-6" /> */}
                  <span className="font-bold text-base mr-6">+</span> {isEpics ? 'Epic' : 'Item'}
                </button>
              )}
            </div>
          </div>
        </Collapse>
      </div>
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
        }}
        isCentered
      >
        <ModalOverlay />
        <ModalContent
          style={{
            maxWidth: '46rem',
            padding: '32px',
            borderRadius: '8px',
          }}
        >
          <ModalCloseButton />
          <p className="mb-32 text-md text-gray-999">Are you sure you wish to proceed?</p>
          <div className="flex justify-end">
            <button
              onClick={() => {
                setIsDeleteModalOpen(false);
              }}
              className="border-blue-500 text-blue-500 border rounded-sm px-24 py-13 mr-8"
            >
              Cancel
            </button>
            <button
              className="border-red-700 text-red-700 border rounded-sm px-24 py-13"
              onClick={handleDeleteSprint}
            >
              Delete
            </button>
          </div>
        </ModalContent>
      </Modal>
      <Portal>
        <Modal isOpen={isEditModalOpen} onClose={handleClose}>
          <ModalOverlay />
          <ModalContent
            style={{
              position: 'initial',
              margin: '80px',
            }}
            className={` min-w-356 bg-white rounded-lg static`}
          >
            <div>
              <EditSprint onClose={handleClose} data={sprint} />
            </div>
            <ModalCloseButton />
          </ModalContent>
        </Modal>
      </Portal>
    </div>
  );
};
